import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';

import SaveIcon from '@mui/icons-material/Save';
import { Box, Theme, Typography } from '@mui/material';

import { handleGetDefendants, mapDefendantsQueryResults } from '../helpers';
import { DeductionCreateFormSubmitProps } from '../types';

import {
  createDeductionRecord,
  getRemittanceContextByCase,
} from '../../../api/paymentsApi/deductionsApi';
import {
  ActiveVendorAccountOption,
  getActiveVendorAccounts,
  getVendorPaymentMethodDetails,
} from '../../../api/vendorApi/vendorAccountsApi';
import { getCase } from '../../../api/casesApi/casesApi';
import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormLinkButton from '../../../components/buttons/FormLinkButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormTextField from '../../../components/formFields/FormTextField';
import FormAutocompleteFieldServerSearch from '../../../components/formFields/FormAutocompleteFieldServerSearch';
import FormToggleField from '../../../components/formFields/FormToggleField';
import FormPDFUploadField from '../../../components/formFields/FormPDFUploadField';
import {
  DEDUCTION_TYPES,
  INTENT_TYPE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
} from '../../../constants/constants';
import { DEDUCTIONS_CREATE_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import { deductionsCreateFormSchema } from '../../../helpers/formSchemas/formSchemas';
import useSnackBar from '../../../hooks/useSnackBar';
import { useHasPermission } from '../../../hooks/useHasPermission';
import SearchCasePopup from '../../../components/SearchCasePopup';
import { Case, CaseShort, PaginationQueryParams } from '../../../types';
import FindCaseButton from '../../../components/buttons/FindCaseButton';
import CaseInfoPreview from '../../../components/CaseInfoPreview';
import { mapVendorsQueryResults } from '../../CasesView/helpers';
import FormDateField from '../../../components/formFields/FormDateField';
import moment from 'moment';
import { getVendorDefaultPaymentAccountId, getVendorPaymentAccountOptions } from '../../../helpers/vendorPaymentAccounts';
import RemittanceAccordion from './RemittanceAccordion';
import Popup from '../../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../../constants/popupDescriptions';
import {
  renderPaymentAccountInfo,
  renderPaymentAccountOption,
  renderPaymentAccountSelectedValue,
} from '../paymentAccountDisplayHelpers';
import { getUser } from '../../../api/usersApi/usersApi';

const DEDUCTION_PAYMENT_METHOD_OPTIONS = PAYMENT_METHOD_OPTIONS.filter(
  (option) => String(option.id).toUpperCase() !== 'V',
);

const useStyles = makeStyles()((theme: Theme) => ({
  formWrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  formContainer: {
    padding: '32px',
    flexBasis: '896px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 850px)': {
      padding: '12px',
    },
  },
  formInfoContainer: {
    display: 'flex',
    gap: '64px',

    '@media (max-width: 850px)': {
      flexDirection: 'column',
      gap: 0,
    },
  },
  formInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '416px',

    '@media (max-width: 850px)': {
      flexBasis: 0,
    },
  },
  formSecondInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '416px',

    '@media (max-width: 850px)': {
      flexBasis: 0,
      marginTop: '32px',
    },
  },
  typeSectionHeadline: {
    marginTop: '40px',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',

    '@media (max-width: 850px)': {
      marginTop: '32px',
    },
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },

}));

const CreateDeductionFormContainer = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();
  const canViewVendors = useHasPermission('Vendors', 'View');

  const caseId = new URLSearchParams(window.location.search).get('caseId');
  const firmId = localStorage.getItem('firmId');

  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string>('');
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [caseDetails, setCaseDetails] = useState<Case | CaseShort | null>(null);
  const [currentUser, setCurrentUser] = useState<{ firm: { name: string } } | null>(null);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(deductionsCreateFormSchema),
    defaultValues: DEDUCTIONS_CREATE_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isValid, isDirty },
  } = formMethods;

  const previousDeductionAmountRef = useRef<string | null>(null);

  const isAssignedToggleValue = watch('isAssigned');
  const defendantFieldValue = watch('defendant.id');
  const typeFieldValue = watch('type');
  const vendorAccountIdValue = watch('vendorAccount.id');
  const vendorAccountValue = watch('vendorAccount');
  const paymentMethodValue = watch('paymentMethod');
  const paymentAccountValue = watch('paymentAccount');
  const dispositionValue = watch('disposition');
  const remittanceLinesValue = watch('remittanceLines') || [];
  const amountValue = watch('amount') ?? '';


  const [activeVendors, setActiveVendors] = useState<ActiveVendorAccountOption[]>([]);
  const [remittanceContext, setRemittanceContext] = useState<any | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [remittanceTemplate, setRemittanceTemplate] = useState<any | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [paymentAccountOptions, setPaymentAccountOptions] = useState<Array<{
    id: string;
    label: string;
    subtitle?: string;
    isLastUsedAccount?: boolean;
    isUsedAccount?: boolean;
    isNotUsedAccount?: boolean;
    isFailedAccount?: boolean;
  }>>([]);

  const selectedPaymentAccount = paymentAccountOptions.find(
    (item) => item.id === String(paymentAccountValue?.id),
  );
  const [isFailedAccountConfirmOpen, setIsFailedAccountConfirmOpen] = useState<boolean>(false);
  const pendingSubmitValuesRef = useRef<DeductionCreateFormSubmitProps | null>(null);
  const [vendorPaymentAccountsById, setVendorPaymentAccountsById] = useState<
    Record<string, { achAccounts?: ActiveVendorAccountOption['achAccounts']; checkAccounts?: ActiveVendorAccountOption['checkAccounts'] }>
  >({});
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [hasRequestedRemittanceContext, setHasRequestedRemittanceContext] = useState(false);
  const [isRemittanceFormOpen, setIsRemittanceFormOpen] = useState(false);

  // Calculate the total amount of remittance lines and check if it balances with the deduction amount for check payments
  const remittanceTotal = Array.isArray(remittanceLinesValue)
    ? remittanceLinesValue.reduce((total, line) => {
      if (typeof line.amount === 'number' && Number.isFinite(line.amount)) {
        return total + line.amount;
      }
      return total;
    }, 0)
    : 0;
  const deductionAmountNumber = Number(amountValue || 0);

  const isCheckRemittanceBalanced =
    (paymentMethodValue || '').toUpperCase() === 'CHECK' &&
      !!remittanceContext &&
      !!remittanceTemplate
      ? Math.round(deductionAmountNumber * 100) ===
      Math.round(remittanceTotal * 100)
      : true;



  // Helper to get activePaymentMethod for selected vendor

  const [isPaymentMethodMissing, setIsPaymentMethodMissing] = useState(false);
  const [isPaymentProvisioningIssue, setIsPaymentProvisioningIssue] = useState(false);

  useEffect(() => {
    const selectedVendor = activeVendors.find(
      (vendor) => String(vendor.id) === String(vendorAccountIdValue)
    );
    const selectedActivePaymentMethod = selectedVendor?.activePaymentMethod || [];
    setIsPaymentMethodMissing(
      !!paymentMethodValue && !selectedActivePaymentMethod.includes(paymentMethodValue)
    );
  }, [paymentMethodValue, vendorAccountIdValue, activeVendors]);

  useEffect(() => {
    const selectedVendor = activeVendors.find(
      (vendor) => String(vendor.id) === String(vendorAccountIdValue),
    );

    const normalizedPaymentMethod = (paymentMethodValue || '').trim().toUpperCase();
    const selectedPaymentAccountId = paymentAccountValue?.id
      ? String(paymentAccountValue.id)
      : '';

    if (!selectedVendor || !selectedPaymentAccountId) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const provisioningList =
      normalizedPaymentMethod === 'ACH'
        ? selectedVendor.activeBankAccounts || []
        : normalizedPaymentMethod === 'CHECK'
          ? selectedVendor.activeMailingAddresses || []
          : [];

    if (!provisioningList.length) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const selectedProvisioningAccount = provisioningList.find(
      (account) => String(account.id) === selectedPaymentAccountId,
    );

    setIsPaymentProvisioningIssue(
      !!selectedProvisioningAccount && selectedProvisioningAccount.provisionStatus !== 'Completed',
    );
  }, [paymentMethodValue, paymentAccountValue?.id, vendorAccountIdValue, activeVendors]);

  const buildDefaultRemittanceValues = (
    template: { columns?: Array<{ binding: string; key: string }> } | null,
    defaults: Record<string, string>,
  ): Record<string, string> => {
    const columns = template && Array.isArray(template?.columns) ? template.columns : [];
    const values: Record<string, string> = {};

    for (const column of columns) {
      if (column.binding === 'LINE_AMOUNT') {
        continue;
      }

      values[column.key] = defaults[column.key] ?? '';
    }

    return values;
  };

  const getDefaultRemittanceLine = (
    template: { columns?: Array<{ binding: string; key: string }> } | null,
    defaults: Record<string, string>,
  ) => {
    const rawAmount = String(amountValue ?? '').trim();
    const parsedAmount = rawAmount ? Number(rawAmount.replace(/[$,\s]/g, '')) : null;

    return {
      id: 1,
      amount: Number.isFinite(parsedAmount as number) ? parsedAmount : null,
      values: buildDefaultRemittanceValues(template, defaults),
    };
  };

  const handleSetCaseValue = (item: CaseShort) => {
    setIsPopupOpen(false);
    setCaseDetails(item);
  };

  useEffect(() => {
    const isCheckPayment =
      (paymentMethodValue || '').toUpperCase() === 'CHECK';

    const firmIdNumber = firmId ? Number(firmId) : null;

    if (!isCheckPayment || !caseDetails?.id || !firmIdNumber) {
      setHasRequestedRemittanceContext(false);
      setIsContextLoading(false);
      setRemittanceContext(null);
      setRemittanceTemplate(null);

      setValue('remittanceContext', null, {
        shouldDirty: false,
        shouldValidate: true,
      });

      setValue('remittanceLines', undefined, {
        shouldDirty: false,
        shouldValidate: true,
      });


      return;
    }

    setHasRequestedRemittanceContext(true);
    setIsContextLoading(true);

    getRemittanceContextByCase(caseDetails.id, firmIdNumber)
      .then((response) => {
        const context = response?.data ?? null;
        const nextTemplate = context?.remittanceTemplate ?? null;
        const nextPrefetchedValues = context?.prefetchedValues ?? {};

        const hasContext = !!context && !!nextTemplate;

        const hasInitialLines =
          Array.isArray(context?.remittanceLines) &&
          context.remittanceLines.length > 0;

        const defaultLine = hasContext
          ? getDefaultRemittanceLine(
            nextTemplate,
            nextPrefetchedValues,
          )
          : null;

        const nextLines = hasInitialLines
          ? context.remittanceLines
          : hasContext
            ? [defaultLine]
            : [];

        setRemittanceContext(context);
        setRemittanceTemplate(nextTemplate);

        setValue('remittanceContext', context ?? null, {
          shouldDirty: false,
          shouldValidate: true,
        });

        if (nextLines.length === 0) {
          setValue('remittanceLines', undefined, {
            shouldDirty: false,
            shouldValidate: true,
          });

          return;
        }

        setValue('remittanceLines', nextLines, {
          shouldDirty: false,
          shouldValidate: true,
        });
      })
      .catch(() => {
        setRemittanceContext(null);
        setRemittanceTemplate(null);

        setValue('remittanceContext', null, {
          shouldDirty: false,
          shouldValidate: true,
        });

        setValue('remittanceLines', undefined, {
          shouldDirty: false,
          shouldValidate: true,
        });
      })
      .finally(() => {
        setIsContextLoading(false);
      });
  }, [
    paymentMethodValue,
    caseDetails?.id,
    firmId,
    setValue,
  ]);

  useEffect(() => {
    const currentAmountValue = String(amountValue ?? '');
    const previousAmountValue = previousDeductionAmountRef.current;

    // Always remember the latest deduction amount.
    previousDeductionAmountRef.current = currentAmountValue;

    // Initial render / initialization.
    // The default remittance row is already created with the current amount
    // by getDefaultRemittanceLine().
    if (previousAmountValue === null) {
      return;
    }

    // Nothing changed in the deduction amount.
    if (previousAmountValue === currentAmountValue) {
      return;
    }

    const isCheckPayment =
      (paymentMethodValue || '').toUpperCase() === 'CHECK';

    if (
      !isCheckPayment ||
      !remittanceContext ||
      !remittanceTemplate
    ) {
      return;
    }

    const currentLines = getValues('remittanceLines') || [];

    // Only auto-sync when exactly one remittance row exists.
    if (!Array.isArray(currentLines) || currentLines.length !== 1) {
      return;
    }

    const rawAmount = currentAmountValue.trim();

    const parsedAmount = rawAmount
      ? Number(rawAmount.replace(/[$,\s]/g, ''))
      : null;

    const firstLine = currentLines[0];

    const nextAmount =
      Number.isFinite(parsedAmount as number)
        ? parsedAmount
        : null;

    if (firstLine.amount === nextAmount) {
      return;
    }

    setValue(
      'remittanceLines',
      [
        {
          ...firstLine,
          amount: nextAmount,
        },
      ],
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  }, [
    amountValue,
    paymentMethodValue,
    remittanceContext,
    remittanceTemplate,
    getValues,
    setValue,
  ]);

  const submitDeductionForm = async (values: DeductionCreateFormSubmitProps) => {
    setIsFormSubmitting(true);

    try {
      console.log('Form values before submission:', values);
      // Create FormData to handle both form data and file upload
      const formData = new FormData();

      // Map Expense Reimbursement to Advance for API
      const typeValue = values.type === 'Expense Reimbursement' ? 'Advance' : values.type;

      // Add form fields
      formData.append(
        'defendantId',
        !values.defendant.id?.length ? '' : String(values.defendant.id),
      );
      formData.append('caseId', String(caseDetails?.id));
      formData.append('amount', values.amount);
      formData.append('type', typeValue);
      formData.append('disposition', values.disposition || '');
      // formData.append('description', values.description || '');
      formData.append('memo', values.memo || '');

      if (typeValue === 'Lien' || typeValue === 'SERVICE_PROVIDER') {
        formData.append('vendorFirmParticipationId', values.vendorAccount?.id ? String(values.vendorAccount.id) : '',);
        values.paymentMethod && formData.append('paymentMethod', values.paymentMethod || '');
        values.paymentAccount?.id && formData.append('vendorPaymentAccountId', String(values.paymentAccount.id));
      }

      if (dispositionValue === 'External') {
        if (typeValue === 'Advance') {
          formData.append('paymentMethod', values.paymentMethod || '');
        }
        formData.append('externalPaymentReference', values.externalPaymentReference || '');
        const formattedDate = values.externalPaymentDate instanceof Date
          ? values.externalPaymentDate.toISOString()
          : values.externalPaymentDate || '';
        formData.append('externalPaymentDate', formattedDate);
      }

      const hasRemittanceRows = Array.isArray(remittanceLinesValue) && remittanceLinesValue.length > 0;
      if ((values.paymentMethod || '').toUpperCase() === 'CHECK' && remittanceContext && hasRemittanceRows) {
        const payloadRemittanceLines = remittanceLinesValue.map(({ amount, values }) => ({
          amount,
          values,
        }));
        formData.append('remittanceLines', JSON.stringify(payloadRemittanceLines));
      }

      // Add file if selected
      if (values.uploadInvoice) {
        formData.append('document', values.uploadInvoice);
      }
      const res = await createDeductionRecord(formData);
      if (!res.data.error) {
        setIsFormSubmitting(false);
        navigate(`/payments/deduction/${res.data.id}/home`);

        setAlert((prev) => ({
          ...prev,
          message: 'Deduction created successfully.',
          type: 'success',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      setIsFormSubmitting(false);
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'An error occurred while creating the deduction.',
        type: 'error',
      }));
    }
  };

  const onSubmitForm = async (values: DeductionCreateFormSubmitProps) => {
    if (selectedPaymentAccount?.isFailedAccount) {
      pendingSubmitValuesRef.current = values;
      setIsFailedAccountConfirmOpen(true);
      return;
    }

    await submitDeductionForm(values);
  };

  const handleConfirmFailedAccountSubmit = async () => {
    setIsFailedAccountConfirmOpen(false);

    const values = pendingSubmitValuesRef.current;
    pendingSubmitValuesRef.current = null;

    if (values) {
      await submitDeductionForm(values);
    }
  };

  const handleCancelFailedAccountSubmit = () => {
    setIsFailedAccountConfirmOpen(false);
    pendingSubmitValuesRef.current = null;
  };

  const handleCancelChanges = () => {
    reset();
    !caseId && setCaseDetails(null);
  };

  const generateDefaultMemo = (caseDetails: Case | CaseShort) => {
    if (!caseDetails) return '';
    const firmName = currentUser?.firm?.name || 'Your Firm';
    const clientName = `${caseDetails.client.firstName} ${caseDetails.client.lastName}`.trim();
    return `Payment from ${firmName} for Client ${clientName}`;
  };

  const handleGetActiveVendors = async (params: PaginationQueryParams) => {
    // Determine vendor type filter based on deduction type
    let vendorTypeFilter: string | undefined;
    if (typeFieldValue === 'Lien') {
      vendorTypeFilter = 'LIEN_HOLDER';
    } else if (typeFieldValue === 'SERVICE_PROVIDER') {
      vendorTypeFilter = 'SERVICE_PROVIDER';
    }

    const filters = vendorTypeFilter ? { vendorType: vendorTypeFilter } : undefined;
    const response = await getActiveVendorAccounts(params.match, filters);
    setActiveVendors(response?.data);
    return {
      data: {
        resultSet: response.data,
        totalCount: response.data.length,
      },
    };
  };

  // Set default memo when case details or current user change
  useEffect(() => {
    if (caseDetails) {
      const defaultMemo = generateDefaultMemo(caseDetails);
      setValue('memo', defaultMemo, { shouldDirty: false });
    }
  }, [caseDetails, currentUser, setValue]);

  useEffect(() => {
    if (!isAssignedToggleValue) {
      setValue('defendant', { id: '', label: '' });
    }
  }, [isAssignedToggleValue, setValue]);

  useEffect(() => {
    if (vendorAccountValue && vendorAccountValue.defaultPaymentMethod) {
      setValue('paymentMethod', vendorAccountValue.defaultPaymentMethod, { shouldDirty: false });
      setDefaultPaymentMethod(vendorAccountValue.defaultPaymentMethod);
    }
  }, [vendorAccountValue, setValue]);

  useEffect(() => {
    if (!vendorAccountIdValue || !paymentMethodValue) {
      setPaymentAccountOptions([]);
      setValue('paymentAccount', null, { shouldDirty: false, shouldValidate: true });
      return;
    }

    const vendorIdKey = String(vendorAccountIdValue);
    const vendorId = Number(vendorAccountIdValue);

    if (!vendorPaymentAccountsById[vendorIdKey] && Number.isFinite(vendorId)) {
      getVendorPaymentMethodDetails(vendorId)
        .then((response) => {
          setVendorPaymentAccountsById((prev) => ({
            ...prev,
            [vendorIdKey]: {
              achAccounts: response.data.achAccounts,
              checkAccounts: response.data.checkAccounts,
            },
          }));
        })
        .catch(() => {
          setVendorPaymentAccountsById((prev) => ({
            ...prev,
            [vendorIdKey]: {
              achAccounts: [],
              checkAccounts: [],
            },
          }));
        });
    }

    const selectedVendor = activeVendors.find(
      (vendor) => String(vendor.id) === String(vendorAccountIdValue),
    );
    const selectedVendorAccounts = vendorPaymentAccountsById[vendorIdKey];

    const vendorForPaymentAccounts = selectedVendor
      ? {
        ...selectedVendor,
        achAccounts: selectedVendorAccounts?.achAccounts,
        checkAccounts: selectedVendorAccounts?.checkAccounts,
      }
      : selectedVendorAccounts;

    const normalizedPaymentMethod = (paymentMethodValue || '').trim().toUpperCase();

    const accountDetails: Array<{
      id: number | string;
      isLastUsedAccount?: boolean;
      isUsedAccount?: boolean;
      isNotUsedAccount?: boolean;
      isFailedAccount?: boolean;
    }> =
      normalizedPaymentMethod === 'ACH'
        ? selectedVendorAccounts?.achAccounts || []
        : normalizedPaymentMethod === 'CHECK'
          ? selectedVendorAccounts?.checkAccounts || []
          : [];

    const options = getVendorPaymentAccountOptions(vendorForPaymentAccounts, paymentMethodValue).map((item) => {
      const account = accountDetails.find(
        (account) => String(account.id) === String(item.id),
      );

      return {
        id: String(item.id),
        label: item.subtitle ? `${item.label} - ${item.subtitle}` : item.label, subtitle: item.subtitle,
        isLastUsedAccount: account?.isLastUsedAccount,
        isUsedAccount: account?.isUsedAccount,
        isNotUsedAccount: account?.isNotUsedAccount,
        isFailedAccount: account?.isFailedAccount,
      };
    });

    setPaymentAccountOptions(options);

    const defaultAccountId = getVendorDefaultPaymentAccountId(vendorForPaymentAccounts, paymentMethodValue);
    const hasCurrentSelection = !!paymentAccountValue?.id && options.some((item) => item.id === String(paymentAccountValue.id));

    if (hasCurrentSelection) {
      return;
    }

    if (defaultAccountId) {
      const defaultOption = options.find((item) => item.id === String(defaultAccountId));
      if (defaultOption) {
        setValue(
          'paymentAccount',
          {
            id: defaultOption.id,
            label: defaultOption.label,
          },
          { shouldDirty: false, shouldValidate: true },
        );
        return;
      }
    }

    setValue('paymentAccount', null, { shouldDirty: false, shouldValidate: true });
  }, [vendorAccountIdValue, paymentMethodValue, activeVendors, paymentAccountValue?.id, setValue, vendorPaymentAccountsById]);

  useEffect(() => {
    if (dispositionValue === 'External') {
      setValue('paymentMethod', '', { shouldDirty: true });
      setValue('paymentAccount', null, { shouldDirty: true, shouldValidate: true });
    } else if (dispositionValue === 'Pay' || dispositionValue === 'Withhold') {
      if (defaultPaymentMethod) {
        setValue('paymentMethod', defaultPaymentMethod, { shouldDirty: true });
      }
    }
  }, [dispositionValue, defaultPaymentMethod, setValue]);


  // Clear vendor account when type changes to ensure proper filtering
  useEffect(() => {
    if (vendorAccountValue !== null) {
      setValue('vendorAccount', null, { shouldDirty: true });
      setValue('paymentAccount', null, { shouldDirty: true, shouldValidate: true });
    }
  }, [typeFieldValue, setValue]);

  useEffect(() => {
    if (!caseId) return;

    getCase(Number(caseId)).then((res) => {
      setCaseDetails(res.data);
    });

  }, [caseId]);

  useEffect(() => {
    // Fetch current user to get firm name
    const userId = localStorage.getItem('userId');
    if (userId) {
      getUser(userId)
        .then((res) => {
          if (res.data) {
            setCurrentUser(res.data);
          }
        })
        .catch((error) => {
          console.warn('Failed to fetch current user:', error);
        });
    }
    handleGetActiveVendors({ page: 1, size: 1000 })
  }, []);

  const handleTypeChange = (value: string) => {
    setValue('type', value, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('vendorAccount', null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('paymentMethod', '', {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('paymentAccount', null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('remittanceContext', null, {
      shouldDirty: false,
      shouldValidate: true,
    });

    setValue('remittanceLines', undefined, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setRemittanceContext(null);
    setRemittanceTemplate(null);
    setDefaultPaymentMethod('');
    setIsContextLoading(false);
  };

  return (
    <>
      <Box className={styles.classes.formWrapper}>
        <FormProvider {...formMethods}>
          <form className={styles.classes.formContainer}>
            <Box className={styles.classes.formInfoContainer}>
              <Box className={styles.classes.formInfoBlock}>
                <Typography className={styles.classes.fieldHeadline}>Case</Typography>
                <Box mt='16px' display='flex' flexDirection='column' gap='16px'>
                  {caseDetails && <CaseInfoPreview caseItem={caseDetails} />}
                  {!caseId && <FindCaseButton onButtonClick={() => setIsPopupOpen(true)} />}
                </Box>

                <Box display='flex' justifyContent='space-between' alignItems='center' mt='32px'>
                  <Typography className={styles.classes.fieldHeadline}>Defendant</Typography>
                  <FormToggleField name='isAssigned' label='Assigned' disabled={!caseDetails} />
                </Box>

                <Box mt='10px'>
                  <FormAutocompleteFieldServerSearch
                    key={caseDetails?.id ? String(caseDetails.id) : 'no-case'}
                    name="defendant"
                    label="Defendant *"
                    optionsQuery={handleGetDefendants}
                    mapQueryResults={mapDefendantsQueryResults}
                    filters={{ caseId: caseDetails?.id }}
                    disabled={!caseDetails || !isAssignedToggleValue}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    fetchSize={100}
                    minSearchChars={3}
                  />
                </Box>
              </Box>

              <Box className={styles.classes.formSecondInfoBlock}>
                <Typography className={styles.classes.fieldHeadline}>Amount</Typography>
                <Box mt='16px'>
                  <FormTextField name='amount' label='Amount *' valueDefinition='$' />
                </Box>

                <Typography className={styles.classes.typeSectionHeadline}>Type</Typography>

                <Box mt='16px'>
                  <FormSelectField
                    options={DEDUCTION_TYPES}
                    name='type'
                    label='Type *'
                    width='100%'
                    customOnChange={(value) => handleTypeChange(value)}
                  />
                </Box>
              </Box>
            </Box>

            <Box display='flex' width='100%' mt='8px'>
              <Box display='flex' flexDirection='column' width='100%'>
                {/* <Typography className={styles.classes.typeSectionHeadline}>
                  Description
                </Typography>
                <Box mt='16px'>
                  <FormTextField
                    name='description'
                    label='Description'
                    isMultiline
                    isDescription
                  />
                </Box> */}
                {(typeFieldValue === 'Lien' || typeFieldValue === 'SERVICE_PROVIDER') && (
                  <>
                    <Typography className={styles.classes.typeSectionHeadline}>
                      Vendor Account
                    </Typography>
                    <Box mt='16px'>
                      <FormAutocompleteFieldServerSearch
                        key={`vendor-${typeFieldValue}`}
                        name="vendorAccount"
                        label="Vendor Account *"
                        optionsQuery={handleGetActiveVendors}
                        mapQueryResults={mapVendorsQueryResults}
                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                        fetchSize={100}
                        minSearchChars={3}
                      />
                    </Box>
                  </>
                )}



                {((typeFieldValue === 'Lien' || typeFieldValue === 'SERVICE_PROVIDER') && vendorAccountIdValue) && (
                  <>
                    <Typography className={styles.classes.typeSectionHeadline}>
                      Intent
                    </Typography>
                    <Box mt='16px'>
                      <FormSelectField
                        name='disposition'
                        label='Intent *'
                        options={INTENT_TYPE_OPTIONS}
                        width='100%'
                      />
                    </Box>
                    <Typography className={styles.classes.typeSectionHeadline}>
                      Payment Method
                    </Typography>
                    <Box mt='16px'>
                      <FormSelectField
                        name='paymentMethod'
                        label={dispositionValue === 'External' ? 'Payment Method' : 'Payment Method *'}
                        options={DEDUCTION_PAYMENT_METHOD_OPTIONS}
                        width='100%'
                        shouldClearable={dispositionValue === 'External'}
                      />
                    </Box>
                    {dispositionValue !== 'External' && paymentMethodValue && (
                      <Box mt='16px'>
                        <Box mt='16px'>
                          <FormSelectField
                            name='paymentAccount.id'
                            label='Account *'
                            options={paymentAccountOptions}
                            width='100%'
                            renderSelectedValue={renderPaymentAccountSelectedValue}
                            renderOption={renderPaymentAccountOption}
                            customOnChange={(value) => {
                              const selected = paymentAccountOptions.find((item) => item.id === value);
                              setValue(
                                'paymentAccount',
                                selected
                                  ? { id: selected.id, label: selected.label }
                                  : null,
                                { shouldDirty: true, shouldValidate: true },
                              );
                            }}
                          />

                          {renderPaymentAccountInfo(
                            selectedPaymentAccount,
                            vendorAccountIdValue,
                          )}
                        </Box>
                      </Box>
                    )}
                    {((isPaymentMethodMissing || isPaymentProvisioningIssue) && dispositionValue !== 'External') && (
                      <Box display='flex' flexDirection='row' alignItems='center' gap='8px' mt={1}>
                        <Typography color='error' variant='body2' mt='8px'>
                          {isPaymentMethodMissing
                            ? 'Missing Account Details'
                            : 'Some issues with the passport provisioning.'}
                        </Typography>
                        {canViewVendors && <FormLinkButton path={`/vendors/edit-vendor-account/${vendorAccountIdValue}`} />}
                      </Box>)}
                  </>
                )}

                {(typeFieldValue === 'Expense Reimbursement') && (
                  <>
                    <Typography className={styles.classes.typeSectionHeadline}>
                      Intent
                    </Typography>
                    <Box mt='16px'>
                      <FormSelectField
                        name='disposition'
                        label='Intent *'
                        options={INTENT_TYPE_OPTIONS}
                        width='100%'
                      />
                    </Box>
                  </>
                )}
                {(dispositionValue === 'External' && typeFieldValue === 'Expense Reimbursement') && (
                  <>
                    <Typography className={styles.classes.typeSectionHeadline}>
                      Payment Method
                    </Typography>

                    <Box mt='16px'>
                      <FormSelectField
                        name='paymentMethod'
                        label='Payment Method'
                        options={DEDUCTION_PAYMENT_METHOD_OPTIONS}
                        width='100%'
                        shouldClearable={true}
                      />
                    </Box>
                  </>
                )}

                {dispositionValue === 'External' && (((typeFieldValue === 'Lien' || typeFieldValue === 'SERVICE_PROVIDER') && vendorAccountIdValue) || typeFieldValue === 'Expense Reimbursement') && (
                  <>
                    <Box mt='16px'>
                      <FormTextField
                        name='externalPaymentReference'
                        label={'Payment Reference'}
                      />
                    </Box>

                    <Box mt='16px'>
                      <FormDateField
                        name='externalPaymentDate'
                        label={'Payment Date'}
                        maxDate={moment()}
                      />
                    </Box>
                  </>
                )

                }

                <Typography className={styles.classes.typeSectionHeadline}>
                  Upload Invoice
                </Typography>
                <Box mt='16px'>
                  <FormPDFUploadField name='uploadInvoice' label='Upload Invoice' />
                </Box>

                {caseDetails &&
                  (typeFieldValue === 'Lien' || typeFieldValue === 'SERVICE_PROVIDER') &&
                  vendorAccountIdValue &&
                  paymentMethodValue &&
                  paymentMethodValue.toUpperCase() === 'CHECK' &&
                  hasRequestedRemittanceContext && (
                    <>
                      <Typography className={styles.classes.typeSectionHeadline}>Check Memo</Typography>
                      <Typography fontSize='12px' color='text.secondary' mt='5px'>
                        This prints on the memo line of the check.
                      </Typography>

                      <Box mt='20px'>
                        <FormTextField name='memo' label='Check Memo' isMultiline isDescription />
                      </Box>

                      <Box mt={'20px'}>
                        <RemittanceAccordion
                          remittanceContext={remittanceContext}
                          remittanceLines={remittanceLinesValue}
                          deductionAmount={amountValue}
                          onChangeRemittanceLines={(lines) => {
                            setValue('remittanceLines', lines, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                          isLoading={isContextLoading}
                          onFormOpenChange={setIsRemittanceFormOpen}
                        />
                      </Box>
                    </>

                  )}


              </Box>
            </Box>

            <Box display='flex' width='100%' justifyContent='center'>
              <Box
                display='flex'
                mt='32px'
                maxWidth='416px'
                flexDirection='column'
                gap='16px'
                width='100%'
              >
                <FormSubmitButton
                  disabled={
                    !isValid ||
                    isRemittanceFormOpen ||
                    (isAssignedToggleValue && !defendantFieldValue) ||
                    isFormSubmitting ||
                    !caseDetails ||
                    !isCheckRemittanceBalanced ||
                    ((typeFieldValue === 'Lien' || typeFieldValue === 'SERVICE_PROVIDER') && (dispositionValue !== 'External') && (!vendorAccountIdValue || !paymentMethodValue || !!isPaymentMethodMissing || !!isPaymentProvisioningIssue))
                    ||
                    ((typeFieldValue === 'Lien' || typeFieldValue === 'SERVICE_PROVIDER') && dispositionValue !== 'External' && !!paymentMethodValue && !paymentAccountValue?.id)
                  }
                  buttonIcon={<SaveIcon />}
                  buttonText='Save'
                  onClick={handleSubmit(onSubmitForm)}
                />
                <FormCancelButton
                  disabled={caseId ? !isDirty : !(isDirty || caseDetails)}
                  onClick={handleCancelChanges}
                />
              </Box>
            </Box>
          </form>
        </FormProvider>
        <SearchCasePopup
          isOpen={isPopupOpen}
          onMainButtonClick={handleSetCaseValue}
          onSecondaryButtonClick={() => setIsPopupOpen(false)}
        />
        <Popup
          isOpen={isFailedAccountConfirmOpen}
          headlineText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.headlineText}
          contentText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.contentText}
          mainButtonText="Yes"
          secondaryButtonText="No"
          onMainButtonClick={handleConfirmFailedAccountSubmit}
          onSecondaryButtonClick={handleCancelFailedAccountSubmit}
        />
      </Box>
    </>
  );
};

export default CreateDeductionFormContainer;